import { api } from "@/api/client";
import type {
  NeedLine,
  PartiaOut,
  RefName,
  ResolvedScan,
  ResolveRequest,
  ScanIndex,
  ScannedExtra,
} from "@/api/order_check";

// Переиспользуем общие «сканерные» типы (они не зависят от типа документа).
export type {
  CheckStatus,
  NeedLine,
  PartiaOut,
  RefName,
  ResolvedScan,
  ResolveRequest,
  ScanIndex,
  ScanKind,
  ScannedExtra,
} from "@/api/order_check";

/** Статус проверки заказа в списке. `sent`/`discrepancy` — двойное
 *  подтверждение (`two_step.py`): отправлено на приёмку / расхождение на
 *  разборе; едущее важнее завершённого (бэк отдаёт его выше). */
export type TransferCheckStatus =
  | "none"
  | "in_progress"
  | "sent"
  | "discrepancy"
  | "completed";

/** Статус сессии проверки перемещения: `open` → (`sent` → `discrepancy`) →
 *  `completed` | `cancelled`. Без приёмки — `open` → `completed`, как раньше. */
export type TransferSessionStatus =
  | "open"
  | "sent"
  | "discrepancy"
  | "completed"
  | "cancelled";

/** Чем кончилась приёмка: совпало / вернули на пересчёт / провели по
 *  принятому / отменили. */
export type TransferResolution = "match" | "recount" | "by_receipt" | "cancel";

export interface TransferCard {
  ref: string;
  number: string;
  date: string;
  organization_ref: string;
  organization_name: string;
  from_ref: string;
  from_name: string;
  to_ref: string;
  to_name: string;
  qty: number;
  state_ref: string;
  state_name: string;
  posted: boolean;
  /** Комментарий заказа: у завоза в комнату ФБС там названо ЮРЛИЦО. */
  comment: string;
  check_status: TransferCheckStatus;
}

export interface TransferListResponse {
  items: TransferCard[];
  has_more: boolean;
}

export interface TransferListParams {
  q?: string;
  organization_ref?: string;
  from_ref?: string;
  to_ref?: string;
  check_status?: string;
  skip?: number;
}

export async function listTransfers(
  params: TransferListParams,
): Promise<TransferListResponse> {
  const { data } = await api.get<TransferListResponse>(
    "/transfer-check/transfers",
    {
      params: {
        q: params.q || undefined,
        organization_ref: params.organization_ref || undefined,
        from_ref: params.from_ref || undefined,
        to_ref: params.to_ref || undefined,
        check_status: params.check_status || undefined,
        skip: params.skip || undefined,
      },
    },
  );
  return data;
}

export async function listTransferOrganizations(
  q?: string,
): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/transfer-check/organizations", {
    params: { q: q || undefined },
  });
  return data;
}

export async function listWarehouses(q?: string): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/transfer-check/warehouses", {
    params: { q: q || undefined },
  });
  return data;
}

// ── Сессии проверки перемещения ───────────────────────────────────────────

/** Вид сессии в движке приёмки: `order` — проверка перемещения по заказу
 *  (`transfer_check`), `free` — «Перемещение» без заказа (`stock_transfer`):
 *  у него `transfer_ref`/`transfer_number` пустые, номер — `display_number`,
 *  правки и отката заказа нет. */
export type ReceiptKind = "order" | "free";

export interface TransferSessionHeader {
  id: string;
  /** В ответах проверки перемещений и приёмки; поле обязательное, умолчания нет. */
  kind: ReceiptKind;
  display_number: number | null;
  transfer_ref: string;
  transfer_number: string;
  transfer_date: string | null;
  organization_name: string | null;
  from_name: string | null;
  to_name: string | null;
  status: TransferSessionStatus;
  created_at: string;
  completed_at: string | null;
  transfer_doc_ref: string | null;
  transfer_doc_number: string | null;
  /** Провёл ли портал созданный документ (создаётся непроведённым). */
  transfer_doc_posted: boolean;
  comment: string | null;
  /** Перемещение идёт в склад из списка «Приёмки перемещений» или из него
   *  (получатель или отправитель в списке): вместо «Завершить» —
   *  «Отправить», документ 1С — после приёмки получателем. Вычислено при
   *  создании сессии; правка настройки едущие не меняет. */
  requires_receipt: boolean;
  /** ISO; когда и кто отправил на приёмку (логин). */
  sent_at: string | null;
  sent_by_name: string | null;
  /** ISO; когда получатель нажал «Принять». */
  received_at: string | null;
  resolution: TransferResolution | null;
  /** Решение сохранено (`resolution`), а шаги 1С не доведены (заказ/документ/
   *  проведение): то же действие нужно нажать ещё раз — оно доделает. */
  pending_resolution: boolean;
}

export interface TransferItemView {
  id: string;
  scan_kind: string;
  nomenclature_ref: string | null;
  nomenclature_name: string | null;
  nomenclature_article: string | null;
  characteristic_ref: string | null;
  characteristic_name: string | null;
  partia_ref: string | null;
  partia_name: string | null;
  partia_source: string | null;
  qty_scanned: number;
  qty_base: number;
  /** Коэффициент единицы скана: `qty_base = qty_scanned × unit_coefficient`. */
  unit_coefficient: number;
  /** Имя базовой единицы номенклатуры для подписи количеств; пусто/нет —
   *  число без слова (единицу наугад не подставляем). */
  base_unit_name?: string;
  created_at: string;
  /** Код штуки ЧЗ в нормальной форме; не штука — пусто (`lib/scan-ui/marks.ts`). */
  mark?: string;  /** SSCC коробки, если позиция записана её кодом (18 цифр, бэк разбирает и AI 00);
   *  иначе пусто. По нему экран отказывает в повторе коробки (`decideBox`). */
  sscc?: string;
}

export interface TransferItemCreate {
  scan_kind: string;
  raw?: string;
  ean13?: string | null;
  gtin?: string | null;
  serial?: string | null;
  nomenclature_ref: string;
  nomenclature_name?: string;
  nomenclature_article?: string;
  characteristic_ref?: string;
  characteristic_name?: string;
  partia_ref?: string;
  partia_name?: string;
  partia_source?: string;
  unit_ref?: string;
  unit_coefficient?: number;
  qty_scanned?: number;
  qty_base?: number;
}

export interface TransferSessionDetail {
  header: TransferSessionHeader;
  needs: NeedLine[];
  extras: ScannedExtra[];
  items: TransferItemView[];
  qty_needed_total: number;
  qty_scanned_total: number;
}

/** Как изменится строка заказа на перемещение под факт (базовые единицы).
 *  `qty_before` — по ИСХОДНОМУ заказу, `qty_after` — каким заказ станет. */
export interface TransferOrderChange {
  nomenclature_ref: string;
  nomenclature_name: string;
  characteristic_ref: string;
  characteristic_name: string;
  qty_before: number;
  qty_after: number;
  kind: "increase" | "decrease" | "add" | "remove";
  base_unit_name: string;
}

export interface TransferOrderChanges {
  order_number: string;
  changes: TransferOrderChange[];
  /** Отпечаток плана: вернуть в «Завершить» — собранное изменилось после
   *  просмотра → 409 `{code: "plan_changed"}`, заказ не тронут. */
  plan_token: string;
}

/** 409 «С момента просмотра собранное изменилось» от `finalize`. */
export function isPlanChangedError(err: unknown): boolean {
  const r = (err as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const detail = r?.data?.detail as { code?: unknown } | undefined;
  return r?.status === 409 && typeof detail === "object" && detail?.code === "plan_changed";
}

export interface TransferFinalizeResult {
  transfer_doc_ref: string;
  transfer_doc_number: string;
  extras_count: number;
  /** Заказ отличается от исходного — портал переписал его под факт. */
  order_changed: boolean;
  order_changes: TransferOrderChange[];
}

export async function createTransferSession(
  transfer_ref: string,
  force_new = false,
): Promise<TransferSessionHeader> {
  const { data } = await api.post<TransferSessionHeader>(
    "/transfer-check/sessions",
    {
      transfer_ref,
      force_new,
    },
  );
  return data;
}

export async function getTransferSession(
  id: string,
): Promise<TransferSessionDetail> {
  const { data } = await api.get<TransferSessionDetail>(
    `/transfer-check/sessions/${id}`,
  );
  return data;
}

/** Остаток склада-источника по паре (ном, хар) потребности, в БАЗОВЫХ ед.
 *  Остаток 1С как есть — отсканированное в сессии НЕ вычтено. */
export interface SourceStockRow {
  nomenclature_ref: string;
  characteristic_ref: string;
  qty_free: number;
  qty_total: number;
  /** Отправлено ДРУГИМИ перемещениями через приёмку (`sent`/`discrepancy`) и
   *  пока не принято — базовые ед.; гейт остатка вычитает их. */
  in_transit: number;
  /** Резерв по 1С = всего − свободно ДО вычитания «в пути» и без обрезки по 0.
   *  Из `qty_free` его не вывести: там уже вычтено «в пути» и обрезано по 0. */
  qty_reserved: number;
}

export interface TransferSourceStock {
  warehouse_name: string;
  rows: SourceStockRow[];
}

export async function getTransferSourceStock(
  id: string,
): Promise<TransferSourceStock> {
  const { data } = await api.get<TransferSourceStock>(
    `/transfer-check/sessions/${id}/source-stock`,
  );
  return data;
}

/**
 * Скан-ручки строк перемещения по базовому адресу.
 *
 * Тела и ответы ОДИНАКОВЫЕ у отправителя (`/transfer-check/sessions/{id}`) и
 * у получателя (`/transfer-receipts/{id}`, контракт этапов 2–3): строки
 * получателя — те же `TransferItemView`. Поэтому ручки параметризованы
 * адресом, а не скопированы: правка тела в одном месте доезжает до обоих.
 */
export interface TransferScanApi {
  resolve(body: ResolveRequest): Promise<ResolvedScan>;
  scanIndex(): Promise<ScanIndex>;
  partia(nomenclatureRef: string, name: string): Promise<PartiaOut>;
  addItem(body: TransferItemCreate): Promise<TransferItemView>;
  /** Сборная коробка ЦЕЛИКОМ: все строки одной записью или ни одной. */
  addBox(body: { raw: string; items: TransferItemCreate[] }): Promise<TransferItemView[]>;
  deleteItem(itemId: string): Promise<void>;
  /** Правятся ЕДИНИЦЫ скана, `qty_base` считает сервер. */
  setQty(itemId: string, units: number, unitCoefficient: number): Promise<TransferItemView>;
}

export function transferScanApi(base: string): TransferScanApi {
  return {
    async resolve(body) {
      const { data } = await api.post<ResolvedScan>(`${base}/resolve`, body);
      return data;
    },
    async scanIndex() {
      const { data } = await api.get<ScanIndex>(`${base}/scan-index`);
      return data;
    },
    async partia(nomenclatureRef, name) {
      const { data } = await api.get<PartiaOut>(`${base}/partia`, {
        params: { nomenclature_ref: nomenclatureRef, name },
      });
      return data;
    },
    async addItem(body) {
      const { data } = await api.post<TransferItemView>(`${base}/items`, body);
      return data;
    },
    async addBox(body) {
      const { data } = await api.post<TransferItemView[]>(`${base}/items/box`, body);
      return data;
    },
    async deleteItem(itemId) {
      await api.delete(`${base}/items/${itemId}`);
    },
    async setQty(itemId, units, unitCoefficient) {
      const { data } = await api.patch<TransferItemView>(
        `${base}/items/${itemId}/quantity`,
        { units, unit_coefficient: unitCoefficient },
      );
      return data;
    },
  };
}

/** Скан-ручки сессии ОТПРАВИТЕЛЯ. */
const sessionScan = (sessionId: string): TransferScanApi =>
  transferScanApi(`/transfer-check/sessions/${sessionId}`);

export async function resolveTransferScan(
  sessionId: string,
  body: ResolveRequest,
): Promise<ResolvedScan> {
  return sessionScan(sessionId).resolve(body);
}

export async function getTransferScanIndex(
  sessionId: string,
): Promise<ScanIndex> {
  return sessionScan(sessionId).scanIndex();
}

export async function lookupTransferPartia(
  sessionId: string,
  nomenclatureRef: string,
  name: string,
): Promise<PartiaOut> {
  return sessionScan(sessionId).partia(nomenclatureRef, name);
}

export async function addTransferItem(
  sessionId: string,
  body: TransferItemCreate,
): Promise<TransferItemView> {
  return sessionScan(sessionId).addItem(body);
}

/** Сборная коробка в перемещение ЦЕЛИКОМ: все строки одной записью или ни
 *  одной (состав сверяет бэк; гейт остатка — по сумме строк; отказ — 409). */
export async function addTransferBoxItems(
  sessionId: string,
  body: { raw: string; items: TransferItemCreate[] },
): Promise<TransferItemView[]> {
  return sessionScan(sessionId).addBox(body);
}

export async function deleteTransferItem(
  sessionId: string,
  itemId: string,
): Promise<void> {
  return sessionScan(sessionId).deleteItem(itemId);
}

export async function reopenTransferSession(
  id: string,
  clearScans = false,
): Promise<TransferSessionHeader> {
  const { data } = await api.post<TransferSessionHeader>(
    `/transfer-check/sessions/${id}/reopen`,
    { clear_scans: clearScans },
  );
  return data;
}

export async function finalizeTransferSession(
  id: string,
  planToken?: string,
): Promise<TransferFinalizeResult> {
  const { data } = await api.post<TransferFinalizeResult>(
    `/transfer-check/sessions/${id}/finalize`,
    undefined,
    planToken ? { params: { plan_token: planToken } } : undefined,
  );
  return data;
}

/** Предпросмотр для диалога «Завершить»: как изменится заказ под факт.
 *  Ничего не пишет; 409 — правка невозможна (текст бэка — причина). */
export async function getTransferOrderChanges(
  sessionId: string,
): Promise<TransferOrderChanges> {
  const { data } = await api.get<TransferOrderChanges>(
    `/transfer-check/sessions/${sessionId}/order-changes`,
  );
  return data;
}

/** Провести созданное «Перемещение запасов».
 *
 *  Отдельным действием, а не частью завершения: документ создаётся
 *  непроведённым намеренно — «да, товар действительно уехал» решает человек.
 */
export async function postTransfer(
  sessionId: string,
): Promise<TransferSessionHeader> {
  const { data } = await api.post<TransferSessionHeader>(
    `/transfer-check/sessions/${sessionId}/post`,
  );
  return data;
}

/** Сколько единиц взяли одним сканом: со склада берут пачками, а скан даёт
 *  одну упаковку. Правятся ЕДИНИЦЫ, `qty_base` считает сервер. */
export async function setTransferItemQty(
  sessionId: string,
  itemId: string,
  units: number,
  unitCoefficient: number,
): Promise<TransferItemView> {
  return sessionScan(sessionId).setQty(itemId, units, unitCoefficient);
}

/** «Отправить» на приёмку (`requires_receipt`): позиции замораживаются, в 1С
 *  НИЧЕГО не пишется — документ создаст и проведёт «Принять» получателя.
 *  409 — текст бэка (уже отправлено, пусто, гейт остатка, не через приёмку). */
export async function sendTransferSession(
  sessionId: string,
): Promise<TransferSessionHeader> {
  const { data } = await api.post<TransferSessionHeader>(
    `/transfer-check/sessions/${sessionId}/send`,
  );
  return data;
}
